import { SendOutlined } from '@ant-design/icons'
import { Button, Drawer, Input, Skeleton, Typography } from 'antd'
import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import api from '~/services/api'

import {
  AddLogSection,
  Content,
  DrawerBody,
  FieldBlock,
  Footer,
  FormGrid,
  FullWidth,
  HeaderInfo,
  Label,
  LogAvatar,
  LogBubble,
  LogEntry,
  LogSection,
  LogText,
  LogTimestamp,
  SectionTitle,
} from './styles'

const { Text } = Typography
const { TextArea } = Input

function initials(name) {
  if (!name) return 'T'
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
}

export default function InspectionDrawer({ open, onClose, rmaId, inventoryItemsId }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [structuredLogs, setStructuredLogs] = useState([])
  const [resolvedInventoryItemsId, setResolvedInventoryItemsId] = useState(null)
  const [newNote, setNewNote] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const loadData = useCallback(async () => {
    if (!rmaId) return
    setLoading(true)
    try {
      const res = await api.get(`/returns/${rmaId}/current-data`)
      setData(res.data)
      setStructuredLogs(res.data.technician_logs ?? [])
      setResolvedInventoryItemsId(res.data.inventory_items_id ?? inventoryItemsId)
    } catch (err) {
      toast.error('Failed to load RMA details.')
    } finally {
      setLoading(false)
    }
  }, [rmaId, inventoryItemsId])

  useEffect(() => {
    if (open && rmaId) {
      setData(null)
      setStructuredLogs([])
      setResolvedInventoryItemsId(null)
      setNewNote('')
      loadData()
    }
  }, [open, rmaId, loadData])

  const handleAddLog = async () => {
    const note = newNote.trim()
    if (!note) return
    setSubmitting(true)
    try {
      const res = await api.patch(`/returns/${rmaId}/technician-log`, { note, inventory_items_id: resolvedInventoryItemsId })
      setStructuredLogs((prev) => [...prev, res.data.technician_log_entry])
      setNewNote('')
    } catch (err) {
      if (err?.response?.status === 422) {
        toast.error(err.response.data?.error || 'Note is invalid or log would exceed the limit.')
      } else {
        toast.error('Failed to add log entry.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const renderField = (label, value) => (
    <FieldBlock>
      <Label>{label}</Label>
      {loading ? <Skeleton active title={{ width: '80%' }} paragraph={false} /> : <Text>{value || <span style={{ color: '#bbb' }}>—</span>}</Text>}
    </FieldBlock>
  )

  return (
    <Drawer
      title={loading ? <Skeleton active title={{ width: 200 }} paragraph={false} /> : <span>RMA #{data?.id} - Inspection Details</span>}
      open={open}
      onClose={onClose}
      width={680}
      destroyOnClose
    >
      <DrawerBody>
        <Content>
          <HeaderInfo>
            <FormGrid>
              {renderField('Order #', data?.order_num)}
              {renderField('Order Date', data?.order_date)}
              {renderField('Store', data?.store_name)}
              {renderField('Return Reason', data?.return_reason)}
              <FullWidth>{renderField('Customer Comments', data?.customer_comments)}</FullWidth>
              <FullWidth>{renderField('Action Required', data?.action_required)}</FullWidth>
            </FormGrid>
          </HeaderInfo>

          <SectionTitle>Technician Log</SectionTitle>

          {loading && <Skeleton active paragraph={{ rows: 3 }} />}
          {!loading && !data?.technician_log && structuredLogs.length === 0 && (
            <Text type="secondary" style={{ fontSize: 13 }}>
              No log entries yet.
            </Text>
          )}
          {!loading && (data?.technician_log || structuredLogs.length > 0) && (
            <LogSection>
              {data?.technician_log && (
                <LogEntry key="legacy">
                  <LogAvatar title="Legacy">?</LogAvatar>
                  <LogBubble>
                    <LogTimestamp>Legacy log (added before technician-log tracking feature)</LogTimestamp>
                    <LogText>{data.technician_log}</LogText>
                  </LogBubble>
                </LogEntry>
              )}
              {structuredLogs.map((entry) => (
                <LogEntry key={entry.id}>
                  <LogAvatar title={entry.technician_name ?? undefined}>{initials(entry.technician_name)}</LogAvatar>
                  <LogBubble>
                    <LogTimestamp>
                      {entry.technician_name
                        ? `${entry.technician_name} · ${moment(entry.created_at).format('MMM DD, YYYY HH:mm')}`
                        : moment(entry.created_at).format('MMM DD, YYYY HH:mm')}
                    </LogTimestamp>
                    <LogText>{entry.note}</LogText>
                  </LogBubble>
                </LogEntry>
              ))}
            </LogSection>
          )}

          <AddLogSection>
            <TextArea
              placeholder="Add a technician note..."
              rows={3}
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  handleAddLog()
                }
              }}
              disabled={loading || submitting}
              maxLength={3900}
              showCount
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="primary" icon={<SendOutlined />} onClick={handleAddLog} loading={submitting} disabled={!newNote.trim() || loading}>
                Add Note
              </Button>
            </div>
          </AddLogSection>
        </Content>

        <Footer>
          <Button onClick={onClose}>Close</Button>
        </Footer>
      </DrawerBody>
    </Drawer>
  )
}
